import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Select, Store } from "@ngxs/store";
import { LocalSettingsService } from "app/core/storage";
import { MenuItems } from "app/shared/menu-items/menu-items";
import { Observable, Subject } from "rxjs";
import { map, takeUntil } from "rxjs/operators";
import { AnnualDeclarationState } from "~app/company/form/annual-declaration-form/shared/state/annual-declaration.state";
import { AuthUser } from "~app/core/auth/core/auth-user.model";
import { ResetPassword, SignIn } from "~app/core/auth/core/auth.actions";
import { AuthState } from "~app/core/auth/core/auth.state";
import { DownloadPdfDocument } from "~app/inscription/store/year-selection/year-selection.actions";
import {
  AnnualRegistration,
  MemberRegistrationSettings,
  YearInfo,
} from "~app/inscription/store/year-selection/year-selection.model";
import { YearSelectionState } from "~app/inscription/store/year-selection/year-selection.state";
import { SystemRoles } from "~app/shared/security/system-roles";
import { environment } from "~env";

@Component({
    selector: "odq-home",
    templateUrl: "./home-temporary.component.html",
    styleUrls: ["./home.component.scss"],
    standalone: false
})
export class HomeComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  isProduction = environment.production;
  currentYear: number;
  years: string;
  allInscriptionFormSubmitted: boolean;
  isMember: boolean;
  isAssistant: boolean;
  isAuthorized: boolean;
  currentDate: Date = new Date();
  year$ = this.store.select<number>(AnnualDeclarationState.year);
  loading$ = this.store.select<boolean>(YearSelectionState.loading);
  yearsInfos$ = this.store.select<YearInfo[]>(YearSelectionState.yearsInfos);
  
  yearsInfos: YearInfo[];

  @Select(YearSelectionState.annualRegistrations)
  annualRegistrations$: Observable<AnnualRegistration[]>;
  annualRegistrations: AnnualRegistration[];

  @Select(YearSelectionState.memberRegistrationSettings)
  memberRegistrationSettings$: Observable<MemberRegistrationSettings>;

  @Select(AnnualDeclarationState.isDeclarationFormLoading)
  isDeclarationFormLoading$: Observable<boolean>;

  @Select(AuthState.signedIn) signedIn$: Observable<boolean>;
  user$ = this.store.select<AuthUser>(AuthState.user);
  user: AuthUser;

  constructor(
    private localSettingsService: LocalSettingsService,
    public menuItems: MenuItems,
    public translate: TranslateService,
    private router: Router,
    private store: Store
  ) {}

  ngOnInit(): void {
    this.currentYear = new Date().getFullYear();
    this.years = `${this.currentYear}-${this.currentYear + 1}`;

    this.signedIn$.pipe(takeUntil(this.destroy$)).subscribe((isAuthorized) => {
      this.isAuthorized = isAuthorized;
    });

    this.user$
      .pipe(
        takeUntil(this.destroy$),
        map((u) => {
          this.user = u;
          this.isMember = u != null && u.permitNumber != "";
          this.isAssistant =
            u != null &&
            u.roles.find(
              (x) =>
                x == SystemRoles.roleDentalAssistantUnconfirmed ||
                x == SystemRoles.roleDentalAssistant
            ) != undefined;
        })
      )
      .subscribe();

    this.yearsInfos$
      .pipe(
        map((infos) => {
          // WARNING : No inscription forms before 2022
          this.yearsInfos = infos?.filter(
            (info) => info.year > new Date().getFullYear() - 1
          );
          this.allInscriptionFormSubmitted =
            this.yearsInfos?.filter((info) => info.state !== "SUBMITTED")
              ?.length > 0
              ? false
              : true;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.annualRegistrations$
      .pipe(
        map((registrations) => (this.annualRegistrations = registrations)),
        takeUntil(this.destroy$)
      )
      .subscribe();

    if (!this.localSettingsService.getClearCache()) {
      // tslint:disable-next-line: deprecation
      location.reload();
      this.localSettingsService.setClearCache();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectYear(year: number) {
    this.router.navigate(["inscription", year, "form"]);
  }

  pdf(yearInfo: YearInfo) {
    this.store.dispatch(
      new DownloadPdfDocument(yearInfo.pdfDocumentId, yearInfo.year)
    );
  }

  isFormOpened(yearInfo: YearInfo): boolean {
    const registrationPeriod = this.annualRegistrations?.find(
      (ar) => ar.year === yearInfo.year
    );
    return !!registrationPeriod?.formOpeningDate
      ? new Date() >= registrationPeriod.formOpeningDate
      : false;
  }

  login() {
    this.store.dispatch(new SignIn(this.router.url));
  }

  resetPassword() {
    this.store.dispatch(new ResetPassword());
  }

  createUser() {
    window.location.href = environment.identity.accountCreation;
  }
}
